import React, { useState, useEffect, useCallback } from 'react';
import apiService from 'services/apiService';
import { useAuth } from 'hooks/useAuth';
import { PageLoader } from 'components/PageLoader';
import Modal from 'components/Modal';
import { UserPlus, Edit, Trash2, CheckSquare, Square } from 'lucide-react';

const TeamView = () => {
    const { user } = useAuth();
    const [members, setMembers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [editingMember, setEditingMember] = useState(null);
    const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

    const fetchMembers = useCallback(async () => {
        setIsLoading(true);
        try {
            const accountMembers = await apiService.fetchTeamMembers();
            setMembers(accountMembers);
        } catch (error) {
            console.error("Failed to fetch team members:", error);
        } finally {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchMembers();
    }, [fetchMembers]);

    const handleUpdate = async () => {
        if (!editingMember) return;
        try {
            const workspaceIds = user.account.workspaces
                .filter(ws => editingMember.workspaceAccess[ws._id])
                .map(ws => ws._id);
            
            // UPDATED: Include teamType in the update payload
            await apiService.updateUser(editingMember._id, { 
                role: editingMember.role,
                status: editingMember.status,
                teamType: editingMember.teamType,
                workspaceIds: workspaceIds
            });
            setEditingMember(null);
            fetchMembers(); // Refresh the list
        } catch (error) {
            alert(`Error updating user: ${error.message}`);
        }
    };

    const handleDelete = async (memberId) => {
        if (window.confirm('Are you sure you want to permanently delete this user? This cannot be undone.')) {
            try {
                await apiService.deleteUser(memberId);
                fetchMembers(); // Refresh the list
            } catch (error) {
                alert(`Error deleting user: ${error.message}`);
            }
        }
    };

    const handleEditClick = (member) => {
        const workspaceAccess = {};
        user.account.workspaces.forEach(ws => {
            workspaceAccess[ws._id] = ws.members.includes(member._id);
        });
        setEditingMember({ ...member, workspaceAccess });
    };

    if (isLoading) return <PageLoader text="Loading team members..." />;

    return (
        <main className="p-8 space-y-8">
            <div className="flex justify-between items-center">
                <h1 className="text-4xl font-bold text-white">Team Management</h1>
                <button onClick={() => setIsInviteModalOpen(true)} className="bg-[#16e2c3] text-black font-bold py-2 px-4 rounded-md hover:bg-opacity-80 flex items-center gap-2">
                    <UserPlus size={18} />
                    Invite Member
                </button>
            </div>
                         
            <div className="bg-[#0A142C] rounded-lg border border-slate-700">
                <div className="p-6 overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="text-xs text-slate-400 uppercase bg-slate-800/50">
                            <tr>
                                <th className="p-4">Name</th>
                                <th className="p-4">Email</th>
                                <th className="p-4">Role</th>
                                {/* NEW: Added Team Type column */}
                                <th className="p-4">Team Type</th>
                                <th className="p-4">Last Active</th>
                                <th className="p-4">Status</th>
                                <th className="p-4">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {members.map(member => (
                                <React.Fragment key={member._id}>
                                    <tr className="border-b border-slate-700">
                                        <td className="p-4 text-white">{member.name}</td>
                                        <td className="p-4 text-slate-300">{member.email}</td>
                                        <td className="p-4 text-slate-300">{member.role}</td>
                                        {/* NEW: Display Team Type */}
                                        <td className="p-4 text-slate-300">{member.teamType || 'N/A'}</td>
                                        <td className="p-4 text-slate-300">{new Date(member.lastActive).toLocaleDateString()}</td>
                                        <td className="p-4">
                                            <span className={`px-2 py-1 text-xs rounded-full ${
                                                member.status === 'active' ? 'bg-green-500/20 text-green-300' : 
                                                member.status === 'pending' ? 'bg-yellow-500/20 text-yellow-300' :
                                                'bg-red-500/20 text-red-300'
                                            }`}>
                                                {member.status}
                                            </span>
                                        </td>
                                        <td className="p-4 flex items-center gap-2">
                                            {/* Prevent owner from being edited/deleted */}
                                            {user.account.owner.toString() !== member._id.toString() && (
                                                <>
                                                    <button onClick={() => handleEditClick(member)} className="text-slate-400 hover:text-white"><Edit size={18} /></button>
                                                    <button onClick={() => handleDelete(member._id)} className="text-slate-400 hover:text-red-400"><Trash2 size={18} /></button>
                                                </>
                                            )}
                                        </td>
                                    </tr>
                                    {editingMember?._id === member._id && (
                                        <tr className="bg-slate-900/50">
                                            <td colSpan="7" className="p-4">
                                                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                                                    <div>
                                                        <label className="block text-sm font-medium text-slate-400 mb-1">Role</label>
                                                        <select value={editingMember.role} onChange={(e) => setEditingMember({...editingMember, role: e.target.value})} className="bg-slate-700 border-slate-600 rounded-md p-2 w-full">
                                                            <option>Team Member</option>
                                                            <option>Team Manager</option>
                                                        </select>
                                                    </div>
                                                    {/* NEW: Team Type dropdown in edit view */}
                                                     <div>
                                                        <label className="block text-sm font-medium text-slate-400 mb-1">Team Type</label>
                                                        <select value={editingMember.teamType} onChange={(e) => setEditingMember({...editingMember, teamType: e.target.value})} className="bg-slate-700 border-slate-600 rounded-md p-2 w-full">
                                                            <option>General</option>
                                                            <option>Graphics Team</option>
                                                            <option>Development Team</option>
                                                            <option>Marketing Team</option>
                                                        </select>
                                                    </div>
                                                    <div>
                                                        <label className="block text-sm font-medium text-slate-400 mb-1">Status</label>
                                                        <select value={editingMember.status} onChange={(e) => setEditingMember({...editingMember, status: e.target.value})} className="bg-slate-700 border-slate-600 rounded-md p-2 w-full">
                                                            <option value="active">Active</option>
                                                            <option value="pending">Pending</option>
                                                        </select>
                                                    </div>
                                                    <div className="md:col-span-1">
                                                        <label className="block text-sm font-medium text-slate-400 mb-1">Workspace Access</label>
                                                        <div className="bg-slate-800 p-2 rounded-md space-y-2 max-h-40 overflow-y-auto">
                                                            {user.account.workspaces.map(ws => (
                                                                <label key={ws._id} className="flex items-center gap-2 cursor-pointer p-1 rounded hover:bg-slate-700/50">
                                                                    <input
                                                                         type="checkbox"
                                                                         className="hidden"
                                                                        checked={!!editingMember.workspaceAccess[ws._id]}
                                                                        onChange={(e) => {
                                                                            setEditingMember({
                                                                                ...editingMember,
                                                                                workspaceAccess: {
                                                                                    ...editingMember.workspaceAccess,
                                                                                    [ws._id]: e.target.checked
                                                                                }
                                                                            })
                                                                        }}
                                                                    />
                                                                    {editingMember.workspaceAccess[ws._id] ? <CheckSquare className="text-[#16e2c3]" /> : <Square className="text-slate-500"/>}
                                                                    <span className="text-sm text-slate-200">{ws.name}</span>
                                                                </label>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex justify-end gap-2 mt-4">
                                                    <button onClick={() => setEditingMember(null)} className="bg-slate-600 text-white font-bold py-2 px-4 rounded-md hover:bg-slate-500">Cancel</button>
                                                    <button onClick={handleUpdate} className="bg-[#16e2c3] text-black font-bold py-2 px-4 rounded-md hover:bg-opacity-80">Save Changes</button>
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
            <InviteUserForm 
                isOpen={isInviteModalOpen}
                onClose={() => setIsInviteModalOpen(false)}
                onUserInvited={fetchMembers}
            />
        </main>
    );
};

const InviteUserForm = ({ isOpen, onClose, onUserInvited }) => {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [role, setRole] = useState('Team Member');
    const [password, setPassword] = useState('');
    // NEW: Add state for teamType
    const [teamType, setTeamType] = useState('General');
    const [passwordChangeRequired, setPasswordChangeRequired] = useState(true);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        try {
            // UPDATED: Pass teamType in the API call
            await apiService.inviteUser({ name, email, role, password, teamType, passwordChangeRequired });
            setSuccess(`Invitation sent to ${email}!`);
            // Reset form
            setName('');
            setEmail('');
            setRole('Team Member');
            setPassword('');
            setTeamType('General');
            setPasswordChangeRequired(true);
            onUserInvited(); // Refresh parent component
            setTimeout(() => {
                onClose();
                setSuccess('');
            }, 2000);
        } catch (err) {
            setError(err.response?.data?.message || 'An error occurred.');
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Invite New Member">
            <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                    <label htmlFor="name" className="block mb-2 text-sm font-medium text-slate-300">Full Name</label>
                    <input type="text" id="name" value={name} onChange={e => setName(e.target.value)} required className="bg-slate-700 border border-slate-600 text-white text-sm rounded-lg block w-full p-2.5" />
                </div>
                <div>
                    <label htmlFor="email-invite" className="block mb-2 text-sm font-medium text-slate-300">Email Address</label>
                    <input type="email" id="email-invite" value={email} onChange={e => setEmail(e.target.value)} required className="bg-slate-700 border border-slate-600 text-white text-sm rounded-lg block w-full p-2.5" />
                </div>
                
                <div>
                    <label htmlFor="password-invite" className="block mb-2 text-sm font-medium text-slate-300">Set Initial Password</label>
                    <input type="password" id="password-invite" value={password} onChange={e => setPassword(e.target.value)} required className="bg-slate-700 border border-slate-600 text-white text-sm rounded-lg block w-full p-2.5" />
                </div>

                 <div>
                    <label htmlFor="role-invite" className="block mb-2 text-sm font-medium text-slate-300">Role</label>
                    <select id="role-invite" value={role} onChange={e => setRole(e.target.value)} className="bg-slate-700 border border-slate-600 text-white text-sm rounded-lg block w-full p-2.5">
                        <option>Team Member</option>
                        <option>Team Manager</option>
                    </select>
                </div>
                {/* NEW: Add Team Type dropdown to invite form */}
                <div>
                    <label htmlFor="team-type-invite" className="block mb-2 text-sm font-medium text-slate-300">Team Type</label>
                    <select id="team-type-invite" value={teamType} onChange={e => setTeamType(e.target.value)} className="bg-slate-700 border border-slate-600 text-white text-sm rounded-lg block w-full p-2.5">
                        <option>General</option>
                        <option>Graphics Team</option>
                        <option>Development Team</option>
                        <option>Marketing Team</option>
                    </select>
                </div>
                <div className="flex items-center">
                    <input 
                        id="change-password" 
                        type="checkbox" 
                        checked={passwordChangeRequired} 
                        onChange={(e) => setPasswordChangeRequired(e.target.checked)}
                        className="h-4 w-4 rounded bg-slate-600 border-slate-500 text-teal-500 focus:ring-teal-400"
                    />
                    <label htmlFor="change-password" className="ml-2 text-sm text-slate-300">
                        Require password change on first login
                    </label>
                </div>
                {error && <p className="text-red-400 text-sm mt-2">{error}</p>}
                {success && <p className="text-green-400 text-sm mt-2">{success}</p>}
                <div className="flex justify-end gap-2 pt-4">
                    <button type="button" onClick={onClose} className="bg-slate-600 text-white font-bold py-2 px-4 rounded-md hover:bg-slate-500">Cancel</button>
                    <button type="submit" className="bg-[#16e2c3] text-black font-bold py-2 px-4 rounded-md hover:bg-opacity-80">Send Invite</button>
                </div>
            </form>
        </Modal>
    );
};

export default TeamView;